/**
 * @file event-format.service.js
 * @module EventFormatService
 * @description Display helpers for events. Event dates are calendar days
 * stored at UTC midnight, so they are formatted in UTC.
 */

import moment from "moment-timezone";
import { EVENT_STATUS } from "../constants/events.constants";

/**
 * Formats an event date for display.
 * @param {string|Date} date
 * @returns {string} e.g. "12 Nov 2026", or "-" without date.
 */
export const formatEventDate = date =>
  date ? moment.utc(date).format("DD MMM YYYY") : "-";

/**
 * Formats the event date range for display.
 * @param {Object} event - `startDate`, `endDate`.
 * @returns {string} e.g. "12 Nov 2026 - 15 Nov 2026".
 */
export const formatEventDates = (event = {}) =>
  `${formatEventDate(event.startDate)} - ${formatEventDate(event.endDate)}`;

/**
 * Formats the event status for display.
 * @param {string} status
 * @returns {string}
 */
export const formatEventStatus = status =>
  status === EVENT_STATUS.INACTIVE ? "Inactive" : "Active";

/**
 * Badge class of the event status.
 * @param {string} status
 * @returns {string}
 */
export const getEventStatusClass = status =>
  status === EVENT_STATUS.INACTIVE ? "badge-danger" : "badge-success";

/**
 * Name of the populated event customer: the company, or the person.
 * @param {Object|null} customer - `company_name`, `full_name`.
 * @returns {string}
 */
export const getEventCustomerName = customer =>
  customer ? customer.company_name || customer.full_name || "-" : "-";

const COMPANY_CUSTOMER_TYPES = ["travel_agency", "travel_access"];

/**
 * Whether the customer type belongs to a company (agency or agent), whose
 * options show the company name.
 * @param {string} type
 * @returns {boolean}
 */
export const isCompanyCustomer = type => COMPANY_CUSTOMER_TYPES.includes(type);

/**
 * Label of a customer option (`/customers-options`), as in New Reservation:
 * "Company - First Last" for agencies and agents, "First Last" otherwise.
 * @param {Object} customer - `type`, `company_name`, `first_name`, `last_name`.
 * @returns {string}
 */
export const getCustomerOptionLabel = (customer = {}) => {
  const name = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
  return isCompanyCustomer(customer.type)
    ? `${customer.company_name} - ${name}`
    : name;
};
